import type { Session, User } from '@supabase/supabase-js';
import type { AuthState, Profile, SignUpResult, UserRole } from '../types';
import { supabase, isSupabaseConfigured } from './supabase';

export const authState: AuthState = {
  user: null,
  profile: null,
  loading: true,
};

type AuthListener = (state: AuthState) => void;

const listeners = new Set<AuthListener>();

export function onAuthChange(fn: AuthListener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function notify(): void {
  for (const fn of listeners) {
    try {
      fn(authState);
    } catch (err) {
      console.warn('auth listener error', err);
    }
  }
}

export function isSignedIn(): boolean {
  return Boolean(authState.user);
}

export function authReady(): boolean {
  return !authState.loading;
}

function metaString(value: unknown): string | null {
  return typeof value === 'string' && value.length ? value : null;
}

function metaRole(value: unknown): UserRole {
  return value === 'factory' ? 'factory' : 'buyer';
}

async function loadProfile(userId: string): Promise<Profile | null> {
  if (!supabase || !userId) return null;
  const { data, error } = await supabase
    .from('profiles')
    .select('id, role, company, created_at')
    .eq('id', userId)
    .maybeSingle();
  if (error) {
    console.warn('profile load failed:', error.message);
    return null;
  }
  if (!data) return null;
  return {
    id: data.id as string,
    role: metaRole(data.role),
    company: (data.company as string | null) ?? null,
    created_at: data.created_at as string | undefined,
  };
}

/**
 * Ensure a profiles row exists (covers race before DB trigger / missing migration).
 */
export async function ensureProfile(
  user: User,
  meta: { role?: UserRole; company?: string } = {},
): Promise<Profile | null> {
  if (!supabase || !user) return null;
  const role = meta.role || metaRole(user.user_metadata?.role) || 'buyer';
  const company = meta.company ?? metaString(user.user_metadata?.company);
  const { data, error } = await supabase
    .from('profiles')
    .upsert(
      {
        id: user.id,
        role: role === 'factory' ? 'factory' : 'buyer',
        company: company || null,
      },
      { onConflict: 'id' },
    )
    .select('id, role, company, created_at')
    .single();
  if (error) {
    console.warn('profile upsert failed (run migration 003?):', error.message);
    return await loadProfile(user.id);
  }
  return {
    id: data.id as string,
    role: metaRole(data.role),
    company: (data.company as string | null) ?? null,
    created_at: data.created_at as string | undefined,
  };
}

async function setSession(session: Session | null): Promise<void> {
  authState.user = session?.user ?? null;
  if (authState.user) {
    authState.profile =
      (await loadProfile(authState.user.id)) ||
      (await ensureProfile(authState.user));
  } else {
    authState.profile = null;
  }
  authState.loading = false;
  notify();
}

export async function initAuth(): Promise<void> {
  if (!isSupabaseConfigured || !supabase) {
    authState.loading = false;
    notify();
    return;
  }
  const { data } = await supabase.auth.getSession();
  await setSession(data.session);
  supabase.auth.onAuthStateChange((_event, session) => {
    void setSession(session);
  });
}

/** True when Supabase reports the email as confirmed. */
export function isEmailConfirmed(user: User | null | undefined): boolean {
  if (!user) return false;
  return Boolean(user.email_confirmed_at || user.confirmed_at);
}

export async function signUp({
  email,
  password,
  role,
  company,
}: {
  email: string;
  password: string;
  role: UserRole;
  company?: string;
}): Promise<SignUpResult> {
  if (!supabase) throw new Error('Supabase is not configured');
  const safeRole: UserRole = role === 'factory' ? 'factory' : 'buyer';
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        role: safeRole,
        company: company || '',
      },
    },
  });
  if (error) throw error;

  const user = data.user ?? null;
  let session = data.session ?? null;

  // Pending when Supabase withholds a session (Confirm email on), or when a session
  // exists but the user is explicitly unconfirmed. Auto-confirm projects return a
  // session with email_confirmed_at set — those may proceed.
  const explicitlyUnconfirmed = Boolean(user) && !isEmailConfirmed(user);
  if (!session || !user || explicitlyUnconfirmed) {
    if (session) {
      try {
        await supabase.auth.signOut();
      } catch (err) {
        console.warn('signOut after unconfirmed signup failed:', err);
      }
      session = null;
    }
    return { user, session: null, pendingConfirmation: true };
  }

  authState.profile = await ensureProfile(user, {
    role: safeRole,
    company: company || '',
  });
  return { user, session, pendingConfirmation: false };
}

export async function signIn({
  email,
  password,
}: {
  email: string;
  password: string;
}): Promise<{ user: User; session: Session }> {
  if (!supabase) throw new Error('Supabase is not configured');
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  if (error) throw error;
  return data;
}

export async function signOut(): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export function displayName(): string {
  if (!authState.user) return '';
  return (
    authState.profile?.company ||
    authState.user.email ||
    authState.user.id.slice(0, 8)
  );
}
